import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Star } from "lucide-react";
import { useRateCleaningTask } from "@/hooks/useCleaningSubmissions";
import { toast } from "@/hooks/use-toast";

interface CleanerRatingCardProps {
  taskId: string;
  cleanerName: string;
  existingRating?: number | null;
  existingNote?: string | null;
  ratedAt?: string | null;
  canRate: boolean; // true if checklist_completed && inventory_completed && !rating
}

const CleanerRatingCard = ({
  taskId,
  cleanerName,
  existingRating,
  existingNote,
  ratedAt,
  canRate,
}: CleanerRatingCardProps) => {
  const [selectedRating, setSelectedRating] = useState<number>(0);
  const [hoverRating, setHoverRating] = useState<number>(0);
  const [note, setNote] = useState("");
  const rateTask = useRateCleaningTask();

  const handleSubmitRating = async () => {
    if (selectedRating < 1 || selectedRating > 5) {
      toast({ title: "Error", description: "Please select a rating (1-5 stars)", variant: "destructive" });
      return;
    }

    try {
      await rateTask.mutateAsync({
        taskId,
        rating: selectedRating,
        ratingNote: note.trim() || undefined,
      });
      toast({ title: "Success", description: "Rating submitted" });
    } catch (error) {
      toast({ title: "Error", description: "Failed to submit rating", variant: "destructive" });
    }
  };

  const renderStars = (rating: number, interactive: boolean = false) => {
    return (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            disabled={!interactive}
            onClick={() => interactive && setSelectedRating(star)}
            onMouseEnter={() => interactive && setHoverRating(star)}
            onMouseLeave={() => interactive && setHoverRating(0)}
            className={`${interactive ? "cursor-pointer hover:scale-110 transition-transform" : "cursor-default"}`}
          >
            <Star
              className={`w-6 h-6 ${
                star <= (interactive ? (hoverRating || selectedRating) : rating)
                  ? "fill-yellow-400 text-yellow-400"
                  : "text-muted-foreground"
              }`}
            />
          </button>
        ))}
      </div>
    );
  };

  // Show existing rating
  if (existingRating) {
    return (
      <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-sm font-medium text-amber-900">Rating:</span>
          {renderStars(existingRating)}
        </div>
        {existingNote && (
          <p className="text-sm text-amber-800 mt-1">"{existingNote}"</p>
        )}
        {ratedAt && (
          <p className="text-xs text-amber-600 mt-1">
            Rated on {new Date(ratedAt).toLocaleDateString()}
          </p>
        )}
      </div>
    );
  }

  // Show rating form if can rate
  if (canRate) {
    return (
      <Card className="border-primary/20 bg-primary/5">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Star className="w-4 h-4 text-primary" />
            Rate {cleanerName}'s Cleaning
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <p className="text-xs text-muted-foreground mb-2">How would you rate this cleaning?</p>
            {renderStars(selectedRating, true)}
          </div>
          <div>
            <Textarea
              placeholder="Add a note (optional)"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className="text-sm h-16 resize-none"
            />
          </div>
          <Button
            size="sm"
            onClick={handleSubmitRating}
            disabled={selectedRating === 0 || rateTask.isPending}
          >
            {rateTask.isPending ? "Submitting..." : "Submit Rating"}
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Not ratable and no existing rating - still show rating section so it always appears
  return (
    <div className="p-3 bg-muted/50 border border-muted rounded-lg">
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium text-muted-foreground">Rating:</span>
        <span className="text-sm text-muted-foreground">Not yet rated</span>
      </div>
    </div>
  );
};

export default CleanerRatingCard;